/**
 * Artwork Drawer — UI.
 *
 * Two steps in one panel:
 *   gallery  pick one or more saved artworks
 *   options  per artwork, choose its own product's variant and quantity
 * then add every line to the cart in a single request and go to /cart.
 *
 * Each artwork is bought as the product it was generated on, so a caricature
 * stays a caricature no matter which style page the drawer was opened from.
 *
 * This file knows nothing about the personalizer. It reads artworks from the
 * store and listens for artwork-drawer:changed.
 */
(function () {
  'use strict';

  var store = window.ArtworkDrawerStore;
  if (!store) return;

  var root = null;
  var els = {};
  var CFG = {};

  // The one line item property the customer is meant to READ. Shopify hides
  // every property whose name starts with an underscore, which is why the poster
  // data below says nothing to them — and at checkout, where the theme cannot
  // reach, they are shown the product's stock photo instead of their poster and
  // conclude the upload was lost. This sentence is the only thing that can speak
  // there on a Basic plan.
  //
  // Kept a plain sentence on purpose: the cart page treats values containing a
  // URL, '/uploads/', '/po/' or 'cdn.shopify.com' as file uploads, and a
  // property literally named Preview as a lightbox source. A URL here would also
  // ride into every order export and packing slip, and it is an unexpiring key
  // to the customer's artwork.
  //
  // The same string is written in three places, because the three add-to-cart
  // surfaces share no module: assets/fsh-builder.js, assets/artwork-drawer.js
  // and assets/fishionist-personalizer.js. Change one, change all three.
  var CART_NOTE_NAME = 'Your artwork';
  var CART_NOTE = 'Saved with this order. The image shown in checkout is a stock product photo';

  var state = {
    open: false,
    step: 'gallery',
    selected: [], // artwork ids, in the order they were ticked
    lines: new Map(), // id -> line
    highlightId: null,
    busy: false,
    lastFocus: null
  };

  var productCache = new Map(); // handle -> Promise<product>
  var closeTimer = null;

  // ---- Small helpers ---------------------------------------------------------

  function show(el, visible) {
    if (el) el.hidden = !visible;
  }

  function money(cents) {
    var n = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: CFG.currency || 'USD'
      }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  function DrawerError(message, code) {
    this.message = message;
    this.code = code || 'generic';
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function icon(paths, extra) {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', extra || '3');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    var p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    p.setAttribute('d', paths);
    svg.appendChild(p);
    return svg;
  }

  // ---- Products --------------------------------------------------------------

  // "12x18IN" -> 12. Anything without a leading number sorts last rather than
  // to the front, so an unexpected value cannot silently displace 12x18IN.
  function leadingSize(v) {
    var n = parseInt(String(v), 10);
    return isNaN(n) ? Infinity : n;
  }

  function normalizeProduct(p) {
    var variants = (p.variants || []).map(function (v) {
      return {
        id: v.id,
        options: [v.option1, v.option2, v.option3].filter(function (x) {
          return x !== null && x !== undefined && x !== '';
        }),
        price: v.price,
        compareAtPrice: v.compare_at_price || null,
        available: !!v.available,
        title: v.title
      };
    });

    var options = (p.options || []).map(function (o, i) {
      var name = typeof o === 'string' ? o : o.name;
      var values = typeof o === 'string' ? [] : (o.values || []).slice();
      if (!values.length) {
        // Older payload shape: derive the values from the variants instead.
        variants.forEach(function (v) {
          var val = v.options[i];
          if (val != null && values.indexOf(val) < 0) values.push(val);
        });
      }
      // Size climbs. Shopify stores option values in whatever order makes the
      // chosen default variant come first — With Frame / 16x24IN — which puts
      // 16x24IN ahead of 12x18IN in the payload. Sorting here matches what
      // product-variant-options.liquid does on the product page, so the drawer
      // and the buy box list the sizes the same way.
      //
      // Only the display order moves. Everything downstream matches variants by
      // VALUE, not by position: findVariant, isValueAvailable and defaultValues
      // all read product.variants, which this does not touch. So the default
      // stays With Frame / 16x24IN.
      if (name === 'Size') {
        values.sort(function (a, b) {
          return leadingSize(a) - leadingSize(b);
        });
      }

      return { name: name, values: values };
    });

    return { id: p.id, title: p.title, handle: p.handle, options: options, variants: variants };
  }

  // A request that never answers used to freeze the drawer for the rest of the
  // session: `state.busy` is only cleared when the chain settles, so a hung
  // /compose left the button dead. Every call the button waits on now has a
  // deadline, so the chain always settles and the customer always gets an
  // answer — success or an error they can act on.
  //
  // Promise.race rather than AbortController alone: the abort frees the socket,
  // the race is what guarantees this promise settles even if the fetch does not
  // reject on abort.
  function fetchDeadline(url, options, ms, message) {
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var opts = {};
    for (var k in options || {}) opts[k] = options[k];
    if (ctrl) opts.signal = ctrl.signal;

    var timer = null;
    var timeout = new Promise(function (_, reject) {
      timer = setTimeout(function () {
        if (ctrl) ctrl.abort();
        reject(new DrawerError(message || 'The server took too long. Please try again.', 'timeout'));
      }, ms);
    });

    return Promise.race([fetch(url, opts), timeout]).then(
      function (res) {
        clearTimeout(timer);
        return res;
      },
      function (err) {
        clearTimeout(timer);
        throw err instanceof DrawerError
          ? err
          : new DrawerError(message || 'The server took too long. Please try again.', 'timeout');
      }
    );
  }

  function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);

    var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var promise = fetch(rootPath + 'products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (res.status === 404) throw new DrawerError('This product is no longer available.', 'gone');
        if (!res.ok) throw new DrawerError('Could not load product options. Please try again.', 'http');
        return res.json();
      })
      .then(normalizeProduct);

    // Do not cache a failure: a flaky network should not poison the handle.
    promise.catch(function () {
      productCache.delete(handle);
    });

    productCache.set(handle, promise);
    return promise;
  }

  function findVariant(product, values) {
    return (
      product.variants.filter(function (v) {
        if (v.options.length !== values.length) return false;
        return values.every(function (val, i) {
          return v.options[i] === val;
        });
      })[0] || null
    );
  }

  // A value is offerable when some in stock variant matches it while agreeing
  // with whatever is already picked on the other options.
  function isValueAvailable(product, values, index, val) {
    return product.variants.some(function (v) {
      if (!v.available || v.options[index] !== val) return false;
      return values.every(function (sel, j) {
        return j === index || sel == null || v.options[j] === sel;
      });
    });
  }

  function defaultValues(product) {
    var first =
      product.variants.filter(function (v) {
        return v.available;
      })[0] || product.variants[0];
    return first ? first.options.slice() : [];
  }

  function lineVariant(line) {
    if (!line.product || !line.variantId) return null;
    return (
      line.product.variants.filter(function (v) {
        return v.id === line.variantId;
      })[0] || null
    );
  }

  function total() {
    var sum = 0;
    state.lines.forEach(function (line) {
      var v = lineVariant(line);
      if (v) sum += v.price * line.qty;
    });
    return sum;
  }

  // ---- Gallery ---------------------------------------------------------------

  // The poster as the customer will receive it, at whatever size the caller
  // needs. A background poster that has not been composed yet is still two
  // separate layers, so the subject is laid over its background at the placement
  // the customer left it at. Shared by the gallery card and the options block so
  // the two can never drift apart.
  function buildPreview(entry, className) {
    var box = el('div', className);
    var img = el('img', 'awd__preview-img');
    img.alt = '';
    img.loading = 'lazy';

    if (entry.mode === 'background' && !entry.composedAt) {
      box.classList.add('is-subject');
      if (entry.bgThumbUrl) box.style.backgroundImage = 'url("' + entry.bgThumbUrl + '")';

      var place = entry.placement || { scale: 0.85, xPct: 50, yPct: 50 };
      img.style.height = Math.max(5, Math.min(100, place.scale * 100)) + '%';
      img.style.left = place.xPct + '%';
      img.style.top = place.yPct + '%';
      img.style.transform = 'translate(-50%, -50%)';
    }

    img.src = entry.thumbUrl;
    box.appendChild(img);
    return box;
  }

  // ---- Frame preview (v2 products only) --------------------------------------
  // The frame is drawn by fishionist-frame.css, which only ships with the v2
  // preview modal. On any other page this list is empty and nothing changes.

  function framedHandles() {
    // The drawer's own root carries the list, straight from theme settings, so
    // the framed thumbnail works on every page the drawer opens on. The preview
    // modal is the fallback and only exists on the AI product pages — reading it
    // alone meant a poster lost its frame the moment the customer navigated away.
    var host = document.getElementById('artwork-drawer');
    var raw = (host && host.dataset.frameHandles) || '';
    if (!raw) {
      var modal = document.querySelector('[data-fsh-modal]');
      raw = (modal && modal.dataset.frameHandles) || '';
    }
    return raw
      .split(',')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }

  function frameEnabled(handle) {
    return !!handle && framedHandles().indexOf(handle) > -1;
  }

  // The chosen value of the product's own "Style" option, or null if it has none.
  function styleValue(line) {
    if (!line.product) return null;
    var i = -1;
    line.product.options.forEach(function (o, idx) {
      if (String(o.name).toLowerCase() === 'style') i = idx;
    });
    return i > -1 ? line.values[i] : null;
  }

  // "Poster (With Frame - Ready to Hang)" is framed, "Poster (Without Frame)"
  // is not. Matching on the words rather than a hard-coded string keeps working
  // if the option labels are reworded.
  function isFramedValue(v) {
    return !!v && !/without/i.test(v) && /frame/i.test(v);
  }

  // Always wrapped, framed or not: the wrapper is what carries the thumbnail's
  // width, so an unwrapped thumb had no size of its own. Adding .fsh-frame turns
  // the same box into the moulding drawn by fishionist-frame-v2.css — the very
  // drawing the product page and the cart use, not a copy of it.
  function buildBlockThumb(line) {
    var box = buildPreview(line.entry, 'awd__block-thumb');
    var wrap = el('div', 'awd__block-frame');
    if (frameEnabled(line.entry.handle)) {
      wrap.classList.add('fsh-frame');
      if (isFramedValue(styleValue(line))) wrap.classList.add('is-framed');
    }
    wrap.appendChild(box);
    return wrap;
  }

  function buildCard(entry) {
    var card = el('button', 'awd__card');
    card.type = 'button';
    card.setAttribute('role', 'checkbox');
    card.dataset.id = entry.id;

    var frame = buildPreview(entry, 'awd__card-frame');
    var img = frame.firstChild;
    img.alt = entry.styleLabel ? entry.styleLabel + ' poster' : 'Your poster';

    img.addEventListener('error', function () {
      card.classList.add('is-broken');
      card.setAttribute('aria-disabled', 'true');
      if (!frame.querySelector('.awd__card-note')) {
        frame.appendChild(el('span', 'awd__card-note', 'Image unavailable'));
      }
      syncCard(card);
    });

    var tick = el('span', 'awd__card-tick');
    tick.appendChild(icon('M20 6L9 17l-5-5'));
    frame.appendChild(tick);

    if (entry.styleLabel) frame.appendChild(el('span', 'awd__card-chip', entry.styleLabel));

    card.appendChild(frame);
    card.setAttribute('aria-checked', state.selected.indexOf(entry.id) > -1 ? 'true' : 'false');

    if (entry.id === state.highlightId) card.classList.add('is-highlight');

    card.addEventListener('click', function () {
      if (card.classList.contains('is-gone') || card.classList.contains('is-broken')) return;
      toggleSelect(entry.id);
      card.setAttribute('aria-checked', state.selected.indexOf(entry.id) > -1 ? 'true' : 'false');
      updateFooter();
    });

    return card;
  }

  function syncCard(card) {
    // A card that cannot be bought must not stay ticked.
    var id = card.dataset.id;
    var i = state.selected.indexOf(id);
    if (i > -1) {
      state.selected.splice(i, 1);
      state.lines.delete(id);
      card.setAttribute('aria-checked', 'false');
      updateFooter();
    }
  }

  function markGoneCards() {
    var items = store.list();
    var handles = {};
    items.forEach(function (entry) {
      handles[entry.handle] = true;
    });

    Object.keys(handles).forEach(function (handle) {
      fetchProduct(handle).catch(function (err) {
        if (!err || err.code !== 'gone') return;
        var cards = els.grid.querySelectorAll('.awd__card');
        for (var i = 0; i < cards.length; i++) {
          var entry = store.get(cards[i].dataset.id);
          if (!entry || entry.handle !== handle) continue;
          cards[i].classList.add('is-gone');
          cards[i].setAttribute('aria-disabled', 'true');
          var frame = cards[i].querySelector('.awd__card-frame');
          if (frame && !frame.querySelector('.awd__card-note')) {
            frame.appendChild(el('span', 'awd__card-note', 'No longer available'));
          }
          syncCard(cards[i]);
        }
      });
    });
  }

  function renderGallery() {
    var items = store.list();

    // One append instead of one per card: the product page runs a
    // MutationObserver over the whole body and reacts to every mutation.
    var frag = document.createDocumentFragment();
    items.forEach(function (entry) {
      frag.appendChild(buildCard(entry));
    });

    els.grid.textContent = '';
    els.grid.appendChild(frag);
    show(els.empty, items.length === 0);

    if (state.highlightId) {
      var target = els.grid.querySelector('[data-id="' + cssEscape(state.highlightId) + '"]');
      if (target) {
        target.scrollIntoView({ block: 'center' });
        setTimeout(function () {
          target.classList.remove('is-highlight');
        }, 1300);
      }
      state.highlightId = null;
    }

    markGoneCards();
  }

  function cssEscape(value) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function toggleSelect(id) {
    var i = state.selected.indexOf(id);
    if (i > -1) {
      state.selected.splice(i, 1);
      state.lines.delete(id);
    } else {
      state.selected.push(id);
    }
  }

  // ---- Options ---------------------------------------------------------------

  // What this row would cost: the variant that differs from the one currently
  // chosen ONLY by this value. Same rule as the product page's option rows, so
  // the two quote the same figure for the same choice.
  function rowPrice(line, index, value) {
    var probe = line.values.slice();
    probe[index] = value;
    var v = findVariant(line.product, probe);
    return v ? v.price : null;
  }

  // Display only. The input's value stays the exact option value, which is what
  // findVariant and the cart both read.
  function optionLabel(option, value) {
    if (option.name !== 'Size') return value;
    return String(value).replace(/IN$/, ' in').replace('x', '×');
  }

  function buildOptionField(line, option, index) {
    var field = el('div', 'awd__field');
    var fieldset = el('fieldset', 'awd__opt');
    var legend = el('legend', 'awd__field-label', option.name);
    fieldset.appendChild(legend);

    var wrap = el('div', 'awd__opt-values');
    var name = 'awd-' + line.entry.id + '-o' + index;

    option.values.forEach(function (value, vi) {
      var id = name + '-' + vi;
      var input = document.createElement('input');
      input.type = 'radio';
      input.name = name;
      input.id = id;
      input.value = value;
      input.checked = line.values[index] === value;
      input.disabled = !isValueAvailable(line.product, line.values, index, value);

      var label = el('label', 'awd__opt-row');
      label.setAttribute('for', id);
      label.appendChild(el('span', 'awd__opt-dot'));
      label.appendChild(el('span', 'awd__opt-name', optionLabel(option, value)));

      // Only Size is priced, as on the product page: a price against every value
      // of every option turns a choice into a spreadsheet.
      if (option.name === 'Size') {
        var p = rowPrice(line, index, value);
        if (p != null) label.appendChild(el('span', 'awd__opt-price', money(p)));
      }

      input.addEventListener('change', function () {
        line.values[index] = value;
        resolveLine(line);
        renderOptions();
      });

      wrap.appendChild(input);
      wrap.appendChild(label);
    });

    fieldset.appendChild(wrap);
    field.appendChild(fieldset);
    return field;
  }

  function buildQty(line) {
    var qty = el('div', 'awd__qty');

    var minus = el('button', null, '\u2212');
    minus.type = 'button';
    minus.setAttribute('aria-label', 'Decrease quantity');
    minus.disabled = line.qty <= 1;

    var input = document.createElement('input');
    input.type = 'number';
    input.inputMode = 'numeric';
    input.min = '1';
    input.max = '99';
    input.value = String(line.qty);
    input.setAttribute('aria-label', 'Quantity');

    var plus = el('button', null, '+');
    plus.type = 'button';
    plus.setAttribute('aria-label', 'Increase quantity');
    plus.disabled = line.qty >= 99;

    function setQty(next) {
      line.qty = Math.min(99, Math.max(1, next || 1));
      input.value = String(line.qty);
      minus.disabled = line.qty <= 1;
      plus.disabled = line.qty >= 99;
      updateBlockPrice(line);
      updateFooter();
    }

    minus.addEventListener('click', function () {
      setQty(line.qty - 1);
    });
    plus.addEventListener('click', function () {
      setQty(line.qty + 1);
    });
    input.addEventListener('change', function () {
      setQty(parseInt(input.value, 10));
    });

    qty.appendChild(minus);
    qty.appendChild(input);
    qty.appendChild(plus);
    return qty;
  }

  function updateBlockPrice(line) {
    if (!line.priceEl) return;
    var v = lineVariant(line);
    line.priceEl.textContent = '';
    if (!v) return;

    line.priceEl.appendChild(el('span', 'awd__price-now', money(v.price * line.qty)));
    if (v.compareAtPrice && v.compareAtPrice > v.price) {
      line.priceEl.appendChild(el('span', 'awd__price-was', money(v.compareAtPrice * line.qty)));
    }
  }

  function buildBlock(line) {
    var block = el('div', 'awd__block');

    var head = el('div', 'awd__block-head');
    head.appendChild(buildBlockThumb(line));

    var meta = el('div', 'awd__block-meta');
    meta.appendChild(el('p', 'awd__block-title', 'Your poster'));
    meta.appendChild(
      el(
        'p',
        'awd__block-desc',
        (line.entry.styleLabel ? line.entry.styleLabel + ' \u00b7 ' : '') + 'ready to print'
      )
    );

    if (line.status === 'gone') {
      var remove = el('button', 'awd__remove', 'Remove');
      remove.type = 'button';
      remove.addEventListener('click', function () {
        toggleSelect(line.entry.id);
        if (!state.selected.length) return goGallery(null);
        renderOptions();
        updateFooter();
      });
      meta.appendChild(remove);
    } else {
      var change = el('button', 'awd__change', 'Change');
      change.setAttribute('aria-label', 'Change poster');
      change.type = 'button';
      change.addEventListener('click', function () {
        goGallery(line.entry.id);
      });
      meta.appendChild(change);
    }

    head.appendChild(meta);
    block.appendChild(head);

    if (line.status === 'loading') {
      block.appendChild(el('div', 'awd__skeleton'));
      return block;
    }

    if (line.status === 'gone') {
      block.classList.add('is-gone');
      block.appendChild(el('p', 'awd__unavailable', 'This product is no longer available.'));
      return block;
    }

    line.product.options.forEach(function (option, i) {
      block.appendChild(buildOptionField(line, option, i));
    });

    // Label, stepper and price as three children of one grid rather than the
    // price beside a label+stepper stack: centred against the stack it sat above
    // the middle of the pill, which read as misaligned.
    var row = el('div', 'awd__row');
    row.appendChild(el('span', 'awd__field-label awd__row-label', 'Quantity'));
    row.appendChild(buildQty(line));
    line.priceEl = el('div', 'awd__price');
    row.appendChild(line.priceEl);
    block.appendChild(row);
    updateBlockPrice(line);

    if (!line.variantId) {
      block.appendChild(
        el('p', 'awd__unavailable', 'This combination is not available. Please pick another.')
      );
    }

    return block;
  }

  function renderOptions() {
    var frag = document.createDocumentFragment();
    state.selected.forEach(function (id) {
      var line = state.lines.get(id);
      if (line) frag.appendChild(buildBlock(line));
    });

    els.blocks.textContent = '';
    els.blocks.appendChild(frag);
    updateFooter();
  }

  function resolveLine(line) {
    if (!line.product) return;
    var variant = findVariant(line.product, line.values);
    line.variantId = variant ? variant.id : null;
  }

  function goOptions() {
    // An artwork can expire between being ticked and Continue being pressed,
    // so drop anything the store no longer knows about before moving on.
    state.selected = state.selected.filter(function (id) {
      if (store.get(id)) return true;
      state.lines.delete(id);
      return false;
    });

    if (!state.selected.length) {
      renderGallery();
      updateFooter();
      return;
    }

    state.selected.forEach(function (id) {
      if (state.lines.has(id)) return;
      var entry = store.get(id);
      if (!entry) return;
      state.lines.set(id, {
        entry: entry,
        values: [],
        variantId: null,
        qty: 1,
        product: null,
        status: 'loading',
        priceEl: null
      });
    });

    state.step = 'options';
    show(els.gallery, false);
    show(els.options, true);
    show(els.back, true);
    renderOptions();
    els.body.scrollTop = 0;
    focusPanel(els.back);

    state.selected.forEach(function (id) {
      var line = state.lines.get(id);
      if (!line || line.status !== 'loading') return;

      fetchProduct(line.entry.handle).then(
        function (product) {
          if (!state.lines.has(id)) return;
          line.product = product;
          line.values = defaultValues(product);
          line.status = 'ready';
          resolveLine(line);
          if (state.step === 'options') renderOptions();
        },
        function () {
          if (!state.lines.has(id)) return;
          line.status = 'gone';
          if (state.step === 'options') renderOptions();
        }
      );
    });
  }

  function goGallery(highlightId) {
    state.highlightId = highlightId || null;
    state.step = 'gallery';
    show(els.options, false);
    show(els.gallery, true);
    show(els.back, false);
    clearError();
    renderGallery();
    updateFooter();
    focusPanel(els.close);
  }

  // ---- Footer ----------------------------------------------------------------

  function updateFooter() {
    var picked = state.selected.length;

    // No strapline on the gallery: the posters are the screen, and a sentence of
    // encouragement above them only pushed the artwork further down.
    els.sub.textContent =
      state.step === 'gallery'
        ? ''
        : picked + (picked === 1 ? ' poster selected' : ' posters selected');

    // The button belongs to whatever operation is in flight, on EITHER screen.
    // This guard used to sit inside the options branch only, so stepping back to
    // the gallery relabelled and re-enabled a button that was still busy — and
    // once a request hung, the customer was left pressing an enabled button that
    // silently did nothing for the rest of the session.
    if (state.busy) return;

    if (state.step === 'gallery') {
      els.cta.disabled = picked === 0;
      els.cta.textContent = picked
        ? 'Continue with ' + picked + (picked === 1 ? ' poster' : ' posters')
        : 'Select a poster';
      return;
    }

    var lines = [];
    state.lines.forEach(function (line) {
      lines.push(line);
    });

    var loading = lines.some(function (l) {
      return l.status === 'loading';
    });
    var gone = lines.some(function (l) {
      return l.status === 'gone';
    });
    var unresolved = lines.some(function (l) {
      return l.status === 'ready' && !l.variantId;
    });

    if (!lines.length) {
      els.cta.disabled = true;
      els.cta.textContent = 'Select a poster';
    } else if (loading) {
      els.cta.disabled = true;
      els.cta.textContent = 'Loading options';
    } else if (gone) {
      els.cta.disabled = true;
      els.cta.textContent = 'Remove unavailable posters';
    } else if (unresolved) {
      els.cta.disabled = true;
      els.cta.textContent = 'Select available options';
    } else {
      els.cta.disabled = false;
      els.cta.textContent = 'Add to cart \u00b7 ' + money(total());
    }
  }

  function setBusy(busy, label) {
    state.busy = busy;
    els.cta.disabled = busy;
    if (busy) {
      els.cta.setAttribute('aria-busy', 'true');
      if (label) els.cta.textContent = label;
    } else {
      els.cta.removeAttribute('aria-busy');
      updateFooter();
    }
  }

  function showError(message, link) {
    els.error.textContent = message;
    if (link) {
      els.error.appendChild(document.createTextNode(' '));
      var a = el('a', null, 'View cart');
      a.href = CFG.cartUrl;
      els.error.appendChild(a);
    }
    show(els.error, true);
  }

  function clearError() {
    els.error.textContent = '';
    show(els.error, false);
  }

  // ---- Add to cart -----------------------------------------------------------

  // Background artworks have no print file until the subject is composited onto
  // its background. Do that now, using the placement the customer left behind.
  function ensurePrintUrl(entry) {
    if (entry.printUrl) return Promise.resolve(entry.printUrl);

    if (entry.mode !== 'background') {
      return Promise.reject(new DrawerError('Poster expired. Please create it again.', 'expired'));
    }
    if (!CFG.workerUrl) {
      return Promise.reject(
        new DrawerError('Please finish this poster on its product page.', 'noworker')
      );
    }

    var place = entry.placement || { scale: 0.85, xPct: 50, yPct: 50 };

    return fetchDeadline(
      CFG.workerUrl + '/compose',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: entry.id,
          handle: entry.handle,
          backgroundId: entry.backgroundId,
          scale: place.scale,
          xPct: place.xPct,
          yPct: place.yPct
        })
      },
      90000,
      'Preparing your poster took too long. Please try again.'
    ).then(function (res) {
      return res.json().then(
        function (data) {
          if (res.status === 404) {
            // The server no longer holds the subject: the artwork is gone.
            store.remove(entry.id);
            throw new DrawerError(data.error || 'Poster expired. Please create it again.', 'expired');
          }
          if (!res.ok || data.status !== 'ok' || !data.output_url) {
            throw new DrawerError(data.error || 'Could not finalize your poster. Please try again.');
          }

          store.update(entry.id, {
            printUrl: data.output_url,
            previewUrl: data.preview_url || data.output_url,
            thumbUrl: data.output_url,
            composedAt: Date.now()
          });
          entry.printUrl = data.output_url;
          return data.output_url;
        },
        function () {
          throw new DrawerError('Could not finalize your poster. Please try again.');
        }
      );
    });
  }

  function buildProps(entry) {
    var props = {};
    props[CART_NOTE_NAME] = CART_NOTE;
    props._print_url = String(entry.printUrl);
    props._artwork_id = String(entry.id);
    if (entry.originalUrl) props._original_url = String(entry.originalUrl);
    if (entry.mode === 'background') {
      if (entry.previewUrl) props._preview_url = String(entry.previewUrl);
      if (entry.backgroundId) props._background = String(entry.backgroundId);
      if (entry.placement) props._placement = JSON.stringify(entry.placement);
    }
    return props;
  }

  function dropExpired(id) {
    var i = state.selected.indexOf(id);
    if (i > -1) state.selected.splice(i, 1);
    state.lines.delete(id);
  }

  function cartItemCount() {
    var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(rootPath + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        return (cart.items || []).length;
      })
      .catch(function () {
        return null;
      });
  }

  function addAllToCart() {
    if (state.busy) return;

    var lines = [];
    state.lines.forEach(function (line) {
      lines.push(line);
    });
    if (!lines.length) return;
    var ready = lines.every(function (l) {
      return l.status === 'ready' && l.variantId;
    });
    if (!ready) return;

    clearError();
    setBusy(true, 'Adding to cart');

    // Remembered so a failure can tell the customer whether anything actually
    // made it into the cart, rather than assuming the request was all or nothing.
    var beforeCount = null;
    cartItemCount().then(function (n) {
      beforeCount = n;
    });

    // Compose sequentially: the Worker runs image work in WebAssembly and
    // parallel calls risk its memory ceiling.
    var chain = Promise.resolve();
    lines.forEach(function (line, i) {
      chain = chain.then(function () {
        if (line.entry.printUrl) return null;
        setBusy(true, 'Preparing poster ' + (i + 1) + ' of ' + lines.length);
        return ensurePrintUrl(line.entry);
      });
    });

    chain
      .then(function () {
        setBusy(true, 'Adding to cart');
        return fetchDeadline(
          CFG.cartAddUrl,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: lines.map(function (line) {
                return {
                  id: line.variantId,
                  quantity: line.qty,
                  properties: buildProps(line.entry)
                };
              })
            })
          },
          30000,
          'The cart did not respond. Please try again.'
        );
      })
      .then(function (res) {
        return res.json().then(
          function (data) {
            if (!res.ok) throw new DrawerError(data.description || data.message || null, 'cart');
            return data;
          },
          function () {
            if (!res.ok) throw new DrawerError(null, 'cart');
            return null;
          }
        );
      })
      .then(function () {
        window.location = CFG.cartUrl;
      })
      .catch(function (err) {
        setBusy(false);

        if (err && err.code === 'expired') {
          var expiredId = null;
          lines.forEach(function (line) {
            if (!store.get(line.entry.id)) expiredId = line.entry.id;
          });
          if (expiredId) dropExpired(expiredId);

          renderGallery();
          if (!state.selected.length) {
            goGallery(null);
          } else {
            renderOptions();
          }
          showError('Poster expired. Please create it again.');
          return;
        }

        // A rejected cart request may still have added some lines, so tell the
        // customer what actually happened rather than guessing.
        verifyCart(err, beforeCount);
      });
  }

  function verifyCart(err, beforeCount) {
    var message = (err && err.message) || 'Could not add to cart. Please try again.';
    if (beforeCount === null) return showError(message);

    cartItemCount().then(function (after) {
      if (after !== null && after > beforeCount) {
        showError('Some items were added to your cart.', true);
      } else {
        showError(message);
      }
    });
  }

  // ---- Open / close ----------------------------------------------------------

  function focusPanel(target) {
    if (typeof window.trapFocus === 'function') {
      window.trapFocus(els.panel, target || els.close);
    } else if (target) {
      target.focus();
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape') closeDrawer();
  }

  function openDrawer() {
    if (state.open) return;
    state.open = true;
    state.lastFocus = document.activeElement;
    clearTimeout(closeTimer);
    clearError();

    if (!state.lines.size) state.step = 'gallery';
    show(els.overlay, true);
    show(els.panel, true);

    if (state.step === 'options') {
      show(els.gallery, false);
      show(els.options, true);
      show(els.back, true);
      renderOptions();
    } else {
      show(els.options, false);
      show(els.gallery, true);
      show(els.back, false);
      renderGallery();
    }
    updateFooter();

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        root.classList.add('is-open');
      });
    });

    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', onKeydown);
    els.tab.setAttribute('aria-expanded', 'true');
    focusPanel(els.close);

    // Warm the product data now so the options step is instant. Failures are
    // handled by markGoneCards.
    store.list().forEach(function (entry) {
      fetchProduct(entry.handle).catch(function () {});
    });
  }

  function closeDrawer() {
    if (!state.open) return;
    state.open = false;
    root.classList.remove('is-open');

    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', onKeydown);
    els.tab.setAttribute('aria-expanded', 'false');

    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(state.lastFocus || els.tab);
    }

    // transitionend does not fire under reduced motion, so always keep a timer.
    clearTimeout(closeTimer);
    closeTimer = setTimeout(function () {
      show(els.panel, false);
      show(els.overlay, false);
    }, 350);
  }

  // ---- Badge -----------------------------------------------------------------

  // The tab appears from the FIRST poster. Briefly raised to 2 on the grounds
  // that a single poster is already on screen, which is only true while the
  // customer stays on the page that made it: posters are saved per browser, not
  // per product, and this tab is the only way back to one. At a threshold of 2,
  // a customer who made one poster and then browsed to another product had no
  // route back to it at all.
  var TAB_MIN = 1;

  function renderBadge() {
    var count = store.list().length;
    els.count.textContent = String(count);
    els.countSr.textContent = count === 1 ? '1 saved poster' : count + ' saved posters';
    show(els.tab, count >= TAB_MIN);
  }

  function onStoreChanged() {
    renderBadge();
    // Never rebuild the grid while the customer is choosing options.
    if (state.open && state.step === 'gallery') renderGallery();
    if (state.open) updateFooter();
  }

  // ---- Init ------------------------------------------------------------------

  function init() {
    var all = document.querySelectorAll('#artwork-drawer');
    if (!all.length) return;
    // The theme editor can render the snippet more than once.
    for (var i = 1; i < all.length; i++) all[i].remove();

    root = all[0];
    // Move out of the product info column so no transformed or clipping
    // ancestor can affect a fixed position element.
    if (root.parentNode !== document.body) document.body.appendChild(root);

    CFG = {
      workerUrl: (root.dataset.workerUrl || '').replace(/\/+$/, ''),
      cartAddUrl:
        root.dataset.cartAddUrl || (window.routes && window.routes.cart_add_url) || '/cart/add',
      cartUrl: root.dataset.cartUrl || (window.routes && window.routes.cart_url) || '/cart',
      currency: root.dataset.currency || 'USD'
    };

    store.configure({ max: root.dataset.max, ttlDays: root.dataset.ttlDays });

    els = {
      tab: root.querySelector('[data-awd-tab]'),
      count: root.querySelector('[data-awd-count]'),
      countSr: root.querySelector('[data-awd-count-sr]'),
      overlay: root.querySelector('[data-awd-overlay]'),
      panel: root.querySelector('#awd-panel'),
      back: root.querySelector('[data-awd-back]'),
      close: root.querySelector('[data-awd-close]'),
      sub: root.querySelector('[data-awd-sub]'),
      body: root.querySelector('[data-awd-body]'),
      gallery: root.querySelector('[data-awd-step="gallery"]'),
      options: root.querySelector('[data-awd-step="options"]'),
      grid: root.querySelector('[data-awd-grid]'),
      empty: root.querySelector('[data-awd-empty]'),
      blocks: root.querySelector('[data-awd-blocks]'),
      error: root.querySelector('[data-awd-error]'),
      cta: root.querySelector('[data-awd-cta]')
    };

    root.hidden = false;

    els.tab.addEventListener('click', openDrawer);
    els.close.addEventListener('click', closeDrawer);
    els.overlay.addEventListener('click', closeDrawer);
    els.back.addEventListener('click', function () {
      goGallery(null);
    });
    els.cta.addEventListener('click', function () {
      if (state.step === 'gallery') return goOptions();
      addAllToCart();
    });

    document.addEventListener('artwork-drawer:changed', onStoreChanged);
    renderBadge();

    window.ArtworkDrawer = {
      version: 1,
      store: store,
      open: openDrawer,
      close: closeDrawer
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
